import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import store from "@/store";
import { Mutations, Actions } from "@/store/enums/StoreEnums";
import JwtService from "@/core/services/JwtService";
import appsetting from "@/core/appsetting";
import * as userUtils from "@/store/userUtils";

import * as permissionUtils from "@/router/permissionUtils";

import routeExtract from "./route-extract";

const routeMenus = await routeExtract();

//console.log("routeMenus", routeMenus);

routeMenus.push(
  ...[
    {
      path: "/",
      component: () => import("@/layouts/SystemLayout.vue"),
      children: [
        {
          // the 404 route, when none of the above matches
          path: "/404",
          name: "404",
          component: () =>
            import("@/views/crafted/authentication/Error404.vue"),
          meta: {
            pageTitle: "Error 404",
          },
        },
        {
          path: "/500",
          name: "500",
          component: () =>
            import("@/views/crafted/authentication/Error500.vue"),
          meta: {
            pageTitle: "Error 500",
          },
        },
        {
          path: "/sso/callback",
          name: "sso-callback",
          component: () => import("@/views/sso/Callback.vue"),
          meta: {
            pageTitle: "SSO Callback",
          },
        },
        {
          path: "/sso/validate",
          name: "sso-validate",
          component: () => import("@/views/sso/Validate.vue"),
          meta: {
            pageTitle: "SSO Validate",
          },
        },
      ],
    },
    {
      path: "/",
      component: () => import("@/layouts/AuthLayout.vue"),
      children: [
        {
          // secondary login (username/password), not redirected to the portal
          path: appsetting.sso.secondaryLoginPath,
          name: "secondary-login",
          component: () => import("@/views/sign-in/SignIn.vue"),
          meta: {
            pageTitle: "Sign In",
          },
        },
      ],
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: "/404",
    },
  ]
);

const routes: Array<RouteRecordRaw> = routeMenus;

const router = createRouter({
  // history: createWebHashHistory(),
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  // current page view title
  document.title = `${to.meta.pageTitle} - ${process.env.VUE_APP_NAME}`;

  // reset config to initial state
  store.commit(Mutations.RESET_LAYOUT_CONFIG);

  // verify auth token before each page change
  store.dispatch(Actions.VERIFY_AUTH, { api_token: JwtService.getToken() });

  // already logged in: login pages are not needed, go to the main page
  const isLoginPage =
    to.name === "sign-in" ||
    to.name === "secondary-login" ||
    to.path === "/sign-in" ||
    to.path === appsetting.sso.secondaryLoginPath;
  if (isLoginPage && store.getters.isUserAuthenticated) {
    next({
      name: userUtils.getCurrentUser()?.defaultRedirectComponent || "dashboard",
    });
    return;
  }

  // the old sign-in page is replaced by the portal login when sso is enabled
  // (the secondary login is available at appsetting.sso.secondaryLoginPath)
  if (
    appsetting.sso.enable &&
    !store.getters.isUserAuthenticated &&
    (to.name === "sign-in" || to.path === "/sign-in")
  ) {
    window.location.href = appsetting.sso.noAuthRedirectUrl;
    next(false);
    return;
  }

  // before page access check if page requires authentication
  if (to.meta.middleware == "auth") {
    const fisrtMenu = permissionUtils.getFirstMenuPermission();
    if (store.getters.isUserAuthenticated) {
      // next check permission
      const permissionObject = permissionUtils.getCurrentPermission(to);
      if (permissionObject) {
        console.log("permissionObject", to);
        if (permissionObject.isCanView === true) {
          next();
        } else if (to.name === "user-edit") {
          //All user can edit data own their
          next();
        } else {
          if (to.name === "dashboard" && fisrtMenu === "dashboard")
            next({ name: "dashboard" });
          else {
            if (fisrtMenu) {
              next({ name: fisrtMenu.routeName });
            } else next();
          }
        }
      }
    } else {
      // not logged in: go to the portal main login
      if (appsetting.sso.enable) {
        window.location.href = appsetting.sso.noAuthRedirectUrl;
        next(false);
      } else {
        next({ name: "sign-in" });
      }
    }
  } else {
    next();
  }

  // Scroll page to top on every route change
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "smooth",
  });
});

export default router;
